import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { LOCALES, translate } from "@/lib/i18n";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

const COUNTRIES = [
  { code: "PT", name: "Portugal", currency: "EUR", flag: "🇵🇹" },
  { code: "AO", name: "Angola", currency: "AOA", flag: "🇦🇴" },
];

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("voltix_cart") || "[]"); } catch { return []; }
  });
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem("voltix_wishlist") || "[]"); } catch { return []; }
  });
  const [compare, setCompare] = useState(() => {
    try { return JSON.parse(localStorage.getItem("voltix_compare") || "[]"); } catch { return []; }
  });
  const [country, setCountry] = useState(() => localStorage.getItem("voltix_country") || "PT");
  const [currency, setCurrency] = useState(() => localStorage.getItem("voltix_currency") || "EUR");
  const [settings, setSettings] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem("voltix_theme") || "light");
  const [locale, setLocale] = useState(() => localStorage.getItem("voltix_locale") || "pt");

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    const t = localStorage.getItem("voltix_token");
    if (t) {
      api.get("/auth/me")
        .then((r) => setUser(r.data))
        .catch(() => localStorage.removeItem("voltix_token"))
        .finally(() => setAuthLoaded(true));
    } else {
      setAuthLoaded(true);
    }
  }, []);

  // Theme
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("voltix_theme", theme);
  }, [theme]);

  // Locale + RTL
  useEffect(() => {
    const loc = LOCALES.find((l) => l.code === locale) || LOCALES[0];
    document.documentElement.setAttribute("lang", loc.code);
    document.documentElement.setAttribute("dir", loc.dir);
    localStorage.setItem("voltix_locale", locale);
  }, [locale]);

  useEffect(() => { localStorage.setItem("voltix_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("voltix_wishlist", JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => { localStorage.setItem("voltix_compare", JSON.stringify(compare)); }, [compare]);
  useEffect(() => { localStorage.setItem("voltix_country", country); }, [country]);
  useEffect(() => { localStorage.setItem("voltix_currency", currency); }, [currency]);

  const syncWishlistOnLogin = useCallback(async (localIds) => {
    try {
      const { data } = await api.post("/wishlist/sync", localIds);
      setWishlist(data);
    } catch { /* ignore */ }
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("voltix_token", data.token);
    setUser(data.user);
    // Sync local wishlist to server
    const local = JSON.parse(localStorage.getItem("voltix_wishlist") || "[]");
    syncWishlistOnLogin(local);
    return data.user;
  }, [syncWishlistOnLogin]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("voltix_token", data.token);
    setUser(data.user);
    const local = JSON.parse(localStorage.getItem("voltix_wishlist") || "[]");
    syncWishlistOnLogin(local);
    return data.user;
  }, [syncWishlistOnLogin]);

  const logout = useCallback(() => {
    localStorage.removeItem("voltix_token");
    setUser(null);
    // Keep local wishlist so guest experience continues
  }, []);

  const addToCart = useCallback((product, opts = {}) => {
    setCart((prev) => {
      const key = `${product.id}|${opts.color || ""}|${opts.size || ""}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) return prev.map((i) => i.key === key ? { ...i, quantity: i.quantity + (opts.quantity || 1) } : i);
      return [...prev, {
        key, product_id: product.id, name: product.name, slug: product.slug,
        image: product.image, unit_price_eur: product.sale_price_eur ?? product.price_eur,
        quantity: opts.quantity || 1, color: opts.color || null, size: opts.size || null,
      }];
    });
  }, []);

  const updateQty = useCallback((key, qty) => {
    setCart((prev) => qty <= 0 ? prev.filter((i) => i.key !== key)
      : prev.map((i) => i.key === key ? { ...i, quantity: qty } : i));
  }, []);

  const removeItem = useCallback((key) => setCart((prev) => prev.filter((i) => i.key !== key)), []);
  const clearCart = useCallback(() => setCart([]), []);

  const inWishlist = useCallback((productId) => wishlist.includes(productId), [wishlist]);

  const toggleWishlist = useCallback(async (productId) => {
    const already = wishlist.includes(productId);
    const next = already ? wishlist.filter((x) => x !== productId) : [...wishlist, productId];
    setWishlist(next);
    if (user) {
      try {
        if (already) await api.delete(`/wishlist/${productId}`);
        else await api.post(`/wishlist/${productId}`);
      } catch { /* ignore */ }
    }
    return !already;
  }, [wishlist, user]);

  const inCompare = useCallback((pid) => compare.includes(pid), [compare]);
  const toggleCompare = useCallback((pid) => {
    const already = compare.includes(pid);
    if (already) { setCompare(compare.filter((x) => x !== pid)); return false; }
    if (compare.length >= 4) return null; // limit reached
    setCompare([...compare, pid]);
    return true;
  }, [compare]);
  const clearCompare = useCallback(() => setCompare([]), []);

  const format = useCallback((amountEur) => {
    if (!settings) return `€${amountEur.toFixed(2)}`;
    const c = settings.currencies?.[currency] || { symbol: currency, rate: 1 };
    const val = amountEur * c.rate;
    return `${c.symbol}${val.toLocaleString("pt-PT", { maximumFractionDigits: 2 })}`;
  }, [settings, currency]);

  const t = useCallback((key) => translate(locale, key), [locale]);

  const cartCount = useMemo(() => cart.reduce((a, i) => a + i.quantity, 0), [cart]);
  const cartSubtotalEur = useMemo(() => cart.reduce((a, i) => a + i.unit_price_eur * i.quantity, 0), [cart]);

  const value = {
    user, authLoaded, login, register, logout,
    cart, addToCart, updateQty, removeItem, clearCart, cartCount, cartSubtotalEur,
    wishlist, inWishlist, toggleWishlist,
    compare, inCompare, toggleCompare, clearCompare,
    country, setCountry, currency, setCurrency,
    settings, setSettings, format, countries: COUNTRIES,
    theme, setTheme, locale, setLocale, locales: LOCALES, t,
  };

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}
